import { z } from 'zod';
import { EXPORT_MAX_ROWS } from '../constants';
import type {
  FunnelMetrics,
  FunnelRatios,
  MetricKey,
  Semaphore,
  StageConversion,
  TargetProgress,
} from '../funnel/metrics';
import { METRIC_KEYS } from '../funnel/metrics';
import { yearMonthSchema } from './common';

export const funnelQuerySchema = z.object({
  companyId: z.string().uuid(),
  projectId: z.string().uuid().optional(),
  advisorId: z.string().uuid().optional(),
  /** 'YYYY-MM' (inicio del periodo). Obligatorio si no llega `months`. */
  month: yearMonthSchema.optional(),
  /** 'YYYY-MM' final del periodo (incluido); vacío = solo el mes. Máximo 24 meses. */
  monthTo: yearMonthSchema.optional(),
  /** Meses sueltos 'YYYY-MM' separados por coma (pueden ser salteados); manda sobre month / monthTo. */
  months: z.string().max(200).optional(),
});
export type FunnelQuery = z.infer<typeof funnelQuerySchema>;

/** Con qué se compara el periodo: el anterior del mismo largo (meses seguidos) o los mismos meses del año anterior. */
export type FunnelPreviousKind = 'PERIODO_ANTERIOR' | 'MISMOS_MESES_ANO_ANTERIOR';

export const funnelDetailsQuerySchema = funnelQuerySchema.extend({
  metric: z.enum(METRIC_KEYS),
  mediumCategory: z.string().optional(),
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  format: z.enum(['json', 'csv']).default('json'),
  /** Tope de filas del CSV (por defecto y máximo `EXPORT_MAX_ROWS`); el JSON pagina con offset / limit. */
  max: z.coerce.number().int().min(1).max(EXPORT_MAX_ROWS).default(EXPORT_MAX_ROWS),
});
export type FunnelDetailsQuery = z.infer<typeof funnelDetailsQuerySchema>;

export const targetInputSchema = z.object({
  projectId: z.string().uuid(),
  yearMonth: yearMonthSchema,
  captaciones: z.coerce.number().int().min(0).default(0),
  leads: z.coerce.number().int().min(0).default(0),
  citasGeneradas: z.coerce.number().int().min(0).default(0),
  citasConcretadas: z.coerce.number().int().min(0).default(0),
  visitas: z.coerce.number().int().min(0).default(0),
  separaciones: z.coerce.number().int().min(0).default(0),
  ventas: z.coerce.number().int().min(0).default(0),
});
export type TargetInput = z.infer<typeof targetInputSchema>;

export interface TargetDto {
  projectId: string;
  projectName: string;
  yearMonth: string;
  captaciones: number;
  leads: number;
  citasGeneradas: number;
  citasConcretadas: number;
  visitas: number;
  separaciones: number;
  ventas: number;
}

export interface FunnelSummary {
  companyId: string;
  projectId: string | null;
  advisorId: string | null;
  month: string;
  /** Mes final cuando se pidió un rango (null = un solo mes). */
  monthTo: string | null;
  /** Cantidad de meses del periodo. */
  months: number;
  /** Meses que forman el periodo, ordenados. */
  selectedMonths: string[];
  /** true si van seguidos; false si hay saltos. */
  contiguous: boolean;
  previousKind: FunnelPreviousKind;
  previousMonths: string[];
  /** % del periodo transcurrido (0–1); 1 si ya cerró. */
  elapsedRatio: number;
  /** Sumas del periodo (los ratios se calculan sobre estas sumas). */
  totals: FunnelMetrics;
  /** Promedio por mes del periodo (totales ÷ meses). */
  monthlyAverage: FunnelMetrics;
  /** Mismo largo de periodo, justo antes. */
  previous: FunnelMetrics;
  ratios: FunnelRatios;
  semaphores: Record<keyof FunnelRatios, Semaphore>;
  conversions: StageConversion[];
  /** Metas del mes (suma de proyectos en alcance) y avance. */
  targets: TargetProgress[];
  daily: Array<{ date: string; metrics: FunnelMetrics }>;
  monthly: Array<{ month: string; metrics: FunnelMetrics }>;
  /** Grano por categoría de medio: la unicidad es dentro de cada categoría (no suma el total). */
  byCategory: Array<{ category: string; metrics: FunnelMetrics; isDigital: boolean }>;
  byAdvisor: Array<{ advisorId: string; advisorName: string; metrics: FunnelMetrics }>;
  byProject: Array<{ projectId: string; projectName: string; metrics: FunnelMetrics }>;
  computedAt: string | null;
}

export interface FunnelDetailRow {
  metric: MetricKey;
  date: string;
  leadId: string;
  contactName: string;
  phone: string | null;
  projectName: string;
  advisorName: string | null;
  medium: string | null;
  mediumCategory: string | null;
  entityType: 'LEAD' | 'INTERACTION' | 'DEAL';
  entityId: string;
  /** Detalle específico del hecho (tipo de interacción, resultado, unidad, monto…). */
  detail: string | null;
  subStatus: string | null;
  pipelineStage: string;
}

export interface FunnelDetailsPage {
  metric: MetricKey;
  total: number;
  offset: number;
  rows: FunnelDetailRow[];
}
